// File: providers/DataProvider.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { makeInstantShellsFromIds } from './expert-pre-load-database';
import { useConfig } from '../../hooks/useConfig';
import { useExpertData } from '../../hooks/useExpertData';
import { useExpertSearch } from '../../hooks/useExpertSearch';
import { usePagination } from '../../hooks/usePagination';

// ────────────────────────────────────────────────────────────────────
// SUPABASE
// ────────────────────────────────────────────────────────────────────
const SUPABASE_URL = 'https://whjonxlpfixzxpkheyum.supabase.co';
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY ?? 'your-fallback-anon-key';
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// ────────────────────────────────────────────────────────────────────
// PROVIDER
// ────────────────────────────────────────────────────────────────────
export default function DataProvider({ children }) {
  // Custom hooks
  const { config, categories, globeIconUrl, vipAllRef, vipByCategoryRef, loading: configLoading, error: configError } = useConfig();
  const {
    fetchExpertsByIdsInOrder,
    fetchPriorityExperts,
    backfillMetadata,
    fetchExpertsFromSimilarity,
  } = useExpertData();
  const { handleNext: paginateNext, handlePrev: paginatePrev, resetIndex } = usePagination({ pageSize: 8 });

  // State
  const [activeCategory, setActiveCategory] = useState('all');
  const [allExperts, setAllExperts] = useState([]);
  const [displayed, setDisplayed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Refs
  const allExpertsRef = useRef([]);
  const displayedRef = useRef([]);
  const fetchSeqRef = useRef(0);

  // Search hook (needs backfillMetadata)
  const {
    searchQuery,
    setSearchQuery,
    isSearchMode,
    handleSearch: searchExperts,
    resetSearch,
    fetchSimilarityResults,
  } = useExpertSearch({ backfillMetadata });

  // ── Display Update ────────────────────────────────────────────────
  const updateDisplay = useCallback((list, start = 0) => {
    if (!Array.isArray(list)) {
      setDisplayed([]);
      setLoading(false);
      return;
    }
    const valid = list.filter((e) => (e.isVip ? true : ((e.description || '').trim().length > 0)));
    const slice = valid.slice(start, start + 50);
    setDisplayed(slice);
    displayedRef.current = slice;
    setLoading(false);
  }, []);

  // ── Initial VIP Paint ─────────────────────────────────────────────
  useEffect(() => {
    if (configLoading || !vipAllRef.current.length) return;

    const shells = makeInstantShellsFromIds(vipAllRef.current);
    if (shells.length) {
      setDisplayed(shells.slice(0, 12));
      displayedRef.current = shells.slice(0, 12);
      setAllExperts(shells);
      allExpertsRef.current = shells;
    }
  }, [configLoading, vipAllRef]);

  // ── Fetch All Category ────────────────────────────────────────────
  const fetchAllCategory = useCallback(async (seq) => {
    const vip = await fetchPriorityExperts(vipAllRef.current);
    const vipIds = vip.map(v => Number(v.id));

    // Show VIPs immediately
    const vipFirstPaint = vip.map(({ sortWeight, ...rest }) => rest);
    if (seq === fetchSeqRef.current && vipFirstPaint.length > 0) {
      setAllExperts(vipFirstPaint);
      allExpertsRef.current = vipFirstPaint;
      updateDisplay(vipFirstPaint, 0);
    }

    const allCat = categories.find(c => c.isAllCategory) || categories[0];
    const searchQueryAll = (allCat?.desc || 'expert');

    try {
      const ids = await fetchSimilarityResults(searchQueryAll);
      const shuffled = await fetchExpertsFromSimilarity(ids, vipIds);

      const firstPaint = [
        ...vipFirstPaint,
        ...shuffled.map(({ sortWeight, ...rest }) => rest),
      ];

      // Backfill metadata in background
      queueMicrotask(async () => {
        const enriched = await backfillMetadata(firstPaint);
        if (seq === fetchSeqRef.current) {
          setAllExperts(enriched);
          allExpertsRef.current = enriched;
          updateDisplay(enriched, 0);
        }
      });

      return firstPaint;
    } catch (err) {
      return vipFirstPaint;
    }
  }, [categories, fetchPriorityExperts, fetchSimilarityResults, fetchExpertsFromSimilarity, backfillMetadata, updateDisplay, vipAllRef]);

  // ── Fetch Similarity Category ─────────────────────────────────────
  const fetchSimilarityCategory = useCallback(async (seq, cat) => {
    const catVipIds = (vipByCategoryRef.current[cat.key] || []);
    console.log(`[${cat.key}] VIP IDs (${catVipIds.length}):`, catVipIds);

    const catVip = await fetchExpertsByIdsInOrder(catVipIds, { markVip: true });
    console.log(`[${cat.key}] VIP Experts fetched:`, catVip.length);
    console.log(`[${cat.key}] VIP Expert names:`, catVip.map(e => `${e.id}: ${e.name}`));

    // Show category VIPs immediately
    const catVipFirstPaint = catVip.map(({ sortWeight, ...rest }) => rest);
    if (seq === fetchSeqRef.current && catVipFirstPaint.length > 0) {
      setAllExperts(catVipFirstPaint);
      allExpertsRef.current = catVipFirstPaint;
      updateDisplay(catVipFirstPaint, 0);
    }

    try {
      const ids = await fetchSimilarityResults(cat.desc || '');
      console.log(`[${cat.key}] Similarity results:`, ids.length);

      const shuffled = await fetchExpertsFromSimilarity(ids, catVipIds);
      console.log(`[${cat.key}] After filtering VIPs:`, shuffled.length);

      const firstPaint = [
        ...catVipFirstPaint,
        ...shuffled.map(({ sortWeight, ...rest }) => rest),
      ];
      console.log(`[${cat.key}] Final list: ${catVipFirstPaint.length} VIPs + ${shuffled.length} semantic = ${firstPaint.length} total`);

      // Backfill metadata in background
      queueMicrotask(async () => {
        const enriched = await backfillMetadata(firstPaint);
        if (seq === fetchSeqRef.current) {
          setAllExperts(enriched);
          allExpertsRef.current = enriched;
          updateDisplay(enriched, 0);
        }
      });

      return firstPaint;
    } catch (err) {
      return catVipFirstPaint;
    }
  }, [fetchExpertsByIdsInOrder, fetchSimilarityResults, fetchExpertsFromSimilarity, backfillMetadata, updateDisplay, vipByCategoryRef]);

  // ── Fetch Experts ─────────────────────────────────────────────────
  const fetchExperts = useCallback(
    async (cat, append = false) => {
      if (!cat) return;

      const seq = ++fetchSeqRef.current;
      setLoading(true);

      if (!append) {
        setDisplayed([]);
        displayedRef.current = [];
      }
      setError(null);

      try {
        const list =
          cat?.isAllCategory && !append
            ? await fetchAllCategory(seq)
            : await fetchSimilarityCategory(seq, cat);

        if (seq !== fetchSeqRef.current) return;

        setAllExperts((prev) => {
          const combined = append ? [...prev, ...list] : list;
          allExpertsRef.current = combined;
          return combined;
        });

        updateDisplay(
          append ? allExpertsRef.current : list,
          append ? displayedRef.current.length : 0
        );

        setHasMore(list.length >= 100);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    },
    [fetchAllCategory, fetchSimilarityCategory, updateDisplay]
  );

  // ── Initial Fetch ─────────────────────────────────────────────────
  useEffect(() => {
    if (!categories.length) return;

    const cat = categories.find(c => c.key === activeCategory) || categories[0];
    if (cat) fetchExperts(cat);
  }, [categories, activeCategory, fetchExperts]);

  // ── Pagination Handlers ───────────────────────────────────────────
  const handleNext = async () => {
    const result = paginateNext(allExperts, async () => {
      const cat = categories.find(c => c.key === activeCategory);
      if (cat && hasMore && !loading) {
        await fetchExperts(cat, true);
      }
    });
    updateDisplay(allExperts, result.newIndex);
  };

  const handlePrev = () => {
    const prevIndex = paginatePrev();
    updateDisplay(allExperts, prevIndex);
  };

  // ── Search Handler ────────────────────────────────────────────────
  const handleSearch = async (q = searchQuery) => {
    await searchExperts(q, {
      onStart: () => {
        setLoading(true);
        setDisplayed([]);
      },
      onSuccess: (enriched) => {
        setAllExperts(enriched);
        allExpertsRef.current = enriched;
        updateDisplay(enriched, 0);
        setHasMore(false);
        setLoading(false);
        resetIndex();
      },
      onError: (errorMsg) => {
        setError(errorMsg);
        setAllExperts([]);
        updateDisplay([], 0);
        setHasMore(false);
        setLoading(false);
      },
    });
  };

  const handleResetSearch = () => {
    resetSearch();
    const nextKey = categories.find(c => c.isAllCategory)?.key || categories[0]?.key || 'all';
    setActiveCategory(nextKey);
  };

  // ── Debug Panel ───────────────────────────────────────────────────
  useEffect(() => {
    window.__DP = {
      categories,
      config,
      vip_all: vipAllRef.current,
      vip_by_category: vipByCategoryRef.current,
    };
  }, [categories, config, vipAllRef, vipByCategoryRef]);

  // ── Render ────────────────────────────────────────────────────────
  return children({
    experts: displayed,
    displayedExperts: displayed,
    loading: loading || configLoading,
    error: error || configError,
    activeCategory,
    setActiveCategory,
    categories,
    handleNext,
    handlePrev,
    loadMore: () => {
      const cat = categories.find(c => c.key === activeCategory);
      if (cat) return fetchExperts(cat, true);
    },
    hasMore,
    searchQuery,
    setSearchQuery,
    handleSearch,
    isSearchMode,
    resetSearch: handleResetSearch,
    globeIconUrl,
    config,
    sendToIntercom: config?.send_to_intercom === true,
  });
}
